import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Crown, Timer, Shield, Palette, CheckCircle, Check } from "lucide-react";
import { useUser } from "@/contexts/user";
import { useFeatureFlags } from "@/contexts/FeatureFlagsContext";
import { useToast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { STRIPE_PRICES, type PricingTier } from "@/config/stripe";
import { debug } from "@/utils/debug";

interface PremiumUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgradeSuccess?: () => void;
}

export default function PremiumUpgradeModal({ isOpen, onClose, onUpgradeSuccess }: PremiumUpgradeModalProps) {
  const { user, updateUser } = useUser();
  const { toast } = useToast();
  const { isFeatureEnabled } = useFeatureFlags();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [selectedTier, setSelectedTier] = useState<PricingTier>('annual');

  // Don't show modal if premium upsell is disabled via feature flag
  if (!isFeatureEnabled('premiumUpsellEnabled')) {
    return null;
  }

  useEffect(() => {
    if (isOpen) {
      trackEvent('premium_modal_opened', {
        isPremium: user.isPremium,
        current_page: window.location.pathname
      });
    }
  }, [isOpen, user.isPremium]);

  const handleUpgrade = async () => {
    const priceInfo = STRIPE_PRICES[selectedTier];
    
    // Check if this tier is ready
    if (priceInfo.priceId.includes('PLACEHOLDER')) {
      toast({
        title: "Coming Soon",
        description: "This pricing tier is being set up. Please try another option or contact support.",
        variant: "destructive"
      });
      return;
    }

    trackEvent('premium_upgrade_clicked', {
      isPremium: user.isPremium,
      current_page: window.location.pathname,
      selected_tier: selectedTier
    });
    setIsUpgrading(true);
    
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');

      // Create Stripe checkout session with selected price
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId: priceInfo.priceId },
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        }
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
        
        toast({
          title: "Redirecting to Checkout",
          description: "Opening secure payment page...",
        });
        
        onClose();
      } else {
        throw new Error('No checkout URL received');
      }
      
      onUpgradeSuccess?.();
    } catch (error) {
      debug.error('Error creating checkout:', error);
      toast({
        title: "Checkout Failed",
        description: "There was an error opening the payment page. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUpgrading(false);
    }
  };

  const isOnboarding = window.location.pathname === '/onboarding';

  const premiumFeatures = [
    {
      icon: Timer,
      title: "Unlimited Daily Study Time",
      description: "Break past the 8-hour free tier limit — study on your terms, no caps"
    },
    {
      icon: Timer,
      title: "15–180 Minute Custom Timers",
      description: "Flexible focus sessions that fit your rhythm"
    },
    {
      icon: Timer,
      title: "3 Custom Forge Cycles",
      description: "Create study/break patterns that match your brain"
    },
    {
      icon: CheckCircle,
      title: "Advanced Analytics (3M–1Y)",
      description: "See long-term progress. Stay motivated with real data"
    },
    {
      icon: Palette,
      title: "Premium Themes",
      description: "Exclusive colors designed to keep you inspired"
    },
    {
      icon: Shield,
      title: "Streak Freeze (2/month)",
      description: "Life happens — protect your progress automatically"
    },
    {
      icon: CheckCircle,
      title: "Break Skip Passes",
      description: "Skip 2 breaks/week when momentum demands it"
    },
    {
      icon: Crown,
      title: "Premium Scholar Badge",
      description: "Show your dedication on leaderboards and profiles"
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[calc(100vh-80px)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-col items-center gap-2 text-center">
            <Crown className="w-7 h-7 text-primary" />
            <span className="text-xl">Become a Premium Scholar</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 pb-4">
          <div className="text-center space-y-3">
            <p className="text-base text-foreground font-semibold leading-relaxed">
              You're serious about growth — your tools should match your ambition.
            </p>
            <p className="text-sm text-muted-foreground">
              Choose the plan that works best for you
            </p>
          </div>

          {/* Pricing Tiers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(Object.entries(STRIPE_PRICES) as [PricingTier, typeof STRIPE_PRICES[PricingTier]][]).map(([key, price]) => (
              <button
                key={key}
                onClick={() => setSelectedTier(key)}
                disabled={price.priceId.includes('PLACEHOLDER')}
                className={`relative p-4 rounded-lg border-2 transition-all text-left ${
                  selectedTier === key
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                } ${price.priceId.includes('PLACEHOLDER') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {price.popular && (
                  <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">
                    Most Popular
                  </Badge>
                )}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg">{price.displayName}</h3>
                    {selectedTier === key && <Check className="w-5 h-5 text-primary" />}
                  </div>
                  <div className="text-2xl font-bold">
                    ${price.amount}
                    <span className="text-sm font-normal text-muted-foreground">
                      /{price.interval === 'quarter' ? '3mo' : price.interval === 'year' ? 'year' : 'mo'}
                    </span>
                  </div>
                  {price.savings && (
                    <Badge variant="secondary" className="text-xs">
                      {price.savings}
                    </Badge>
                  )}
                  <p className="text-xs text-muted-foreground">{price.description}</p>
                  {price.priceId.includes('PLACEHOLDER') && (
                    <p className="text-xs text-destructive">Coming soon</p>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Features List */}
          <div className="space-y-2">
            <h4 className="font-semibold text-sm text-center mb-3">All plans include:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {premiumFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-start gap-2 text-sm">
                    <Icon className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">{feature.title}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={handleUpgrade}
              disabled={isUpgrading || STRIPE_PRICES[selectedTier].priceId.includes('PLACEHOLDER')}
              className="w-full gradient-primary h-12 text-lg"
              size="lg"
            >
              {isUpgrading ? (
                "Processing..."
              ) : (
                <>
                  <Crown className="w-4 h-4 mr-2" />
                  Continue to Checkout
                </>
              )}
            </Button>
            
            <Button 
              variant="outline"
              onClick={onClose}
              className="w-full"
            >
              Maybe Later
            </Button>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}